import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';
import ExamSuccessAlert from '../common/alert/ExamSuccessAlertPage';
import GoldErrorAlert from '../common/alert/GoldErrorAlertPage';
import '../css/ExamPage.css';
import { toast } from 'react-toastify';

const ExamPage = () => {
  const navigate = useNavigate();
  const { examId, subjectId, testId } = useParams();

  const [coinNum, setCoinNum] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [responseData, setResponseData] = useState({});
  const [timeLeft, setTimeLeft] = useState(0); 
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchWithToken = async (url, options = {}) => {
    try {
      const tokenString = localStorage.getItem('authToken');
      const token = tokenString ? JSON.parse(tokenString) : null;

      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      const response = await fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          Authorization: `Bearer ${token.access}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Something went wrong.');
      }

      return response.json();
    } catch (err) {
      throw err;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const profileData = await fetchWithToken(`${API_URL}/student/profile/`);
        setCoinNum(profileData.coin_num);

        const questionData = await fetchWithToken(
          `${API_URL}/main/exams/${examId}/subjects/${subjectId}/tests/${testId}/questions`
        );
        if (questionData.objective_time) {
          const [h, m, s] = questionData.objective_time.split(':').map(Number);
          const totalSeconds = h * 3600 + m * 60 + s;
          setTimeLeft(totalSeconds);
        } else {
          setTimeLeft(50 * 60); // fallback 50 minutes
        }
        setQuestions(questionData.questions || []);
      } catch (err) {
        console.error('Error fetching data:', err.message);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [examId, subjectId, testId]);

  const handleSubmit = useCallback(async () => {
    if (Object.keys(selectedOptions).length === 0) {
      toast.error("You must answer at least one question before submitting.");
      return;
    }
    setLoading(true);
    console.log("Payload being sent:", JSON.stringify({ answers: selectedOptions }));
    try {
      const response = await fetchWithToken(
        `${API_URL}/main/submit-answers/${examId}/${subjectId}/${testId}/exam`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ answers: selectedOptions }),
          

        }
        
      );
      
      setResponseData(response);
      setShowSuccessAlert(true);
    } catch (err) {
      console.error('Error submitting answers:', err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [examId, subjectId, testId, selectedOptions]);

  const handleOptionSelect = (questionId, optionId) => {
    setSelectedOptions((prevState) => {
      const updatedState = {
        ...prevState,
        [questionId]: optionId,
      };
      console.log('Updated Selected Options:', updatedState);
      return updatedState;
    });
  };


  useEffect(() => {
    if (timeLeft <= 0) {
      handleSubmit();
    }
    const timer = setInterval(() => setTimeLeft((prev) => Math.max(prev - 1, 0)), 1000);
    return () => clearInterval(timer);
  }, [timeLeft, handleSubmit]);

  
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
  };

  if (loading) return <div>Loading...</div>;

  if (error)
    return (
      <GoldErrorAlert
        title="Insulficient Gold!"
        coinNum={coinNum}
        onClose={() => navigate(`/tests/${examId}/${subjectId}`)}
        buyGold={() => navigate('/golds')}
      />
    );



  const renderContent = (text) => {
    if (!text) return null;

    const parts = text.split(
      /(\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|\\\([\s\S]+?\\\)|\$[^$]+\$)/g
    );

    return parts.map((part, index) => {
      // $$...$$ (block)
      if (part.startsWith("$$") && part.endsWith("$$")) {
        return (
          <div key={index} style={{ margin: "10px 0" }}>
            <BlockMath math={part.slice(2, -2)} />
          </div>
        );
      }

      // \[...\] (block)
      if (part.startsWith("\\[") && part.endsWith("\\]")) {
        return (
          <div key={index} style={{ margin: "10px 0" }}>
            <BlockMath math={part.slice(2, -2)} />
          </div>
        );
      }

      // \(...\) (inline)
      if (part.startsWith("\\(") && part.endsWith("\\)")) {
        return (
          <InlineMath key={index} math={part.slice(2, -2)} />
        );
      }

      // $...$ (inline)
      if (part.startsWith("$") && part.endsWith("$")) {
        return (
          <InlineMath key={index} math={part.slice(1, -1)} />
        );
      }

      // Normal text
      return (
        <span key={index} style={{ whiteSpace: "pre-line" }}>
          {part}
        </span>
      );
    });
  };

  return (
    <div className="main-exam-screen">
      <div className="timer" style={{ color: timeLeft <= 60 ? 'red' : timeLeft <= 120 ? 'yellow' : 'black' }}>
        Time Left: {formatTime(timeLeft)}
      </div>
      <div className="questions-container">
      {questions.map((question, index) => (
          <div key={question.id} className="question-card">
            <p><strong>Question {index + 1}:</strong></p>
            
            {/* Question Image */}
            {question.Eq_img && (
              <img src={question.Eq_img} alt="Question" style={{ maxWidth: '300px', marginBottom: '10px' }} />
            )}
            
            {/* Question Text */}
            <p className="questions-text">{renderContent(question.text)}</p>

            {/* Options */}
            {question.options.map((option) => (
              <button
                key={option.id}
                className={`option ${selectedOptions[question.id] === option.id ? 'selected' : ''}`}
                onClick={() => handleOptionSelect(question.id, option.id)}
              >
                {option.option_letter}. {renderContent(option.text)}

                {/* Option Image */}
                {option.Eoption_img && (
                  <div>
                    <img src={option.Eoption_img} alt="Option" style={{ maxWidth: '150px', marginTop: '5px' }} />
                  </div>
                )}
              </button>
            ))}
          </div>
        ))}

      </div>
      <button className="submit-button" onClick={handleSubmit}>
        Submit
      </button>
      <ExamSuccessAlert
        visible={showSuccessAlert}
        title="Score!"
        message={responseData.message}
        totalQuestion={responseData.total_questions}
        correctCount={responseData.correct_count}
        onClose={() => navigate(`/tests/${examId}/${subjectId}`)}
      />
    </div>
  );
};

export default ExamPage;
